import { useClient } from '@cortex/react-query';
import type { PlatformTool } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type CSSProperties } from 'react';

import {
  fetchAllMcpConnections,
  McpToolsEditor,
  removeToolRef,
  type IMcpToolsValue,
} from '../../mcp-connections/McpToolsEditor';

import { classifyRef, filterPlatformTools } from './platform-tools';

/**
 * Editor for an llm-agent node's `toolRefs` + `toolAllowlists` (DEV-16947,
 * DEV-16944). A ref is resolved at runtime (see
 * packages/node-types/src/llm-agent/resolve-tools.ts) as either:
 *   1. a platform tool id (e.g. `get-devices`), or
 *   2. an MCPConnection name — expands to that server's advertised tools,
 *      optionally restricted by a per-connection allowlist.
 *
 * MCP connections get the shared rich picker (`McpToolsEditor` — the same
 * component the agent editor uses), including per-tool allowlist editing.
 * Platform ids come from `GET /v1/platform-tools` and get a searchable
 * picker below it; refs that match neither surface (typos, tools removed
 * from the registry) render as warning-styled chips. When the registry
 * endpoint is unavailable (older API / fetch failure) the picker degrades
 * to the original free-text input and no ref is flagged as unresolved —
 * we can't tell a valid id from a typo without the registry.
 */
export function ToolRefsField({
  toolRefs,
  toolAllowlists,
  onChange,
}: {
  toolRefs: string[];
  toolAllowlists: Record<string, string[]>;
  onChange: (next: IMcpToolsValue) => void;
}) {
  const client = useClient();
  const [customRef, setCustomRef] = useState('');
  const [search, setSearch] = useState('');
  // The free-text escape hatch stays available even when the picker is up —
  // collapsed behind a small affordance so the common path is pick-from-list.
  const [showCustom, setShowCustom] = useState(false);

  // Same query key + fetch as McpToolsEditor — react-query dedupes, so this
  // costs nothing extra. Needed here only to partition refs into
  // connection-name vs platform/custom.
  const connections = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: () => fetchAllMcpConnections(client.http),
  });
  const connectionNames = useMemo(
    () => new Set((connections.data?.items ?? []).map((c) => c.name)),
    [connections.data],
  );
  // Refs must never be flagged as unresolved while the connections list is
  // loading or errored — `connectionNames` would be spuriously empty and a
  // perfectly valid connection ref would render as a deletable "⚠ unresolved
  // ref" chip. (Platform-registry readiness is already encoded in
  // `platformIds === null` below.)
  const registriesReady = connections.isSuccess;

  // Static registry → cache long; a page refresh is an acceptable staleness
  // bound for a list that changes only on API deploys.
  const platformTools = useQuery({
    queryKey: ['platform-tools'],
    staleTime: 30 * 60_000,
    queryFn: () => client.http.get('v1/platform-tools').json<{ items: PlatformTool[] }>(),
  });
  const registry = platformTools.data?.items ?? null;
  const platformIds = useMemo(
    () => (registry ? new Set(registry.map((t) => t.id)) : null),
    [registry],
  );
  const platformById = useMemo(() => new Map((registry ?? []).map((t) => [t.id, t])), [registry]);

  // Refs that don't match a known connection name — platform tool ids or
  // custom refs typed in by hand. Rendered as removable chips.
  const nonConnectionRefs = useMemo(
    () => toolRefs.filter((ref) => !connectionNames.has(ref)),
    [toolRefs, connectionNames],
  );

  const value: IMcpToolsValue = { toolRefs, toolAllowlists };

  const addRef = (ref: string) => {
    const trimmed = ref.trim();
    if (trimmed.length === 0 || toolRefs.includes(trimmed)) return;
    onChange({ toolRefs: [...toolRefs, trimmed], toolAllowlists });
  };

  const pickerMatches = registry ? filterPlatformTools(registry, search, toolRefs) : [];

  const freeTextRow = (
    <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
      <input
        className="input"
        value={customRef}
        onChange={(e) => setCustomRef(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addRef(customRef);
            setCustomRef('');
          }
        }}
        placeholder="Platform tool id (e.g. get-devices)"
        style={{ flex: 1 }}
      />
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => {
          addRef(customRef);
          setCustomRef('');
        }}
        disabled={customRef.trim().length === 0}
      >
        Add
      </button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <span className="field-label">Tools</span>

      <McpToolsEditor
        toolRefs={toolRefs}
        toolAllowlists={toolAllowlists}
        onChange={onChange}
        showUnknownRefs={false}
      />

      {nonConnectionRefs.length > 0 && (
        <div style={chipRow}>
          {nonConnectionRefs.map((ref) => {
            const kind = classifyRef(ref, connectionNames, platformIds, registriesReady);
            const unresolved = kind === 'unknown';
            return (
              <span
                key={ref}
                style={unresolved ? unknownChip : customChip}
                title={unresolved ? 'unresolved ref' : (platformById.get(ref)?.description ?? ref)}
              >
                {unresolved ? <span aria-hidden="true">⚠ </span> : null}
                {ref}
                <button
                  type="button"
                  aria-label={`Remove tool ref ${ref}`}
                  onClick={() => onChange(removeToolRef(value, ref))}
                  style={removeBtn}
                >
                  ✕
                </button>
              </span>
            );
          })}
        </div>
      )}

      {registry ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <span className="field-label">Add platform tool</span>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search platform tools…"
            aria-label="Search platform tools"
          />
          {pickerMatches.length === 0 ? (
            <p className="t-xs t-muted" style={{ margin: 0 }}>
              {registry.length === 0
                ? 'No platform tools available.'
                : search.trim().length > 0
                  ? 'No platform tools match — use “Add custom id” for unlisted ids.'
                  : 'Every platform tool is already attached.'}
            </p>
          ) : (
            <div style={pickerList}>
              {pickerMatches.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => addRef(t.id)}
                  title={t.description}
                  style={pickerRow}
                >
                  <span className="t-mono t-xs" style={{ color: 'var(--text-primary)' }}>
                    {t.id}
                  </span>
                  <span className="t-xs t-muted" style={pickerDescription}>
                    {t.description}
                  </span>
                </button>
              ))}
            </div>
          )}
          {showCustom ? (
            freeTextRow
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setShowCustom(true)}
            >
              + Add custom id
            </button>
          )}
        </div>
      ) : (
        // Registry unavailable (loading, older API, or fetch failure) —
        // degrade to the original free-text input.
        freeTextRow
      )}
      <span className="field-help">
        Expand a referenced connection above to restrict which of its tools this node may call.
        Platform/custom ids resolve against the tool registry at runtime.
      </span>
    </div>
  );
}

const chipRow: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };

const customChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  padding: '3px 8px',
  borderRadius: 4,
  cursor: 'default',
  fontFamily: 'var(--font-mono, monospace)',
  border: '1px solid var(--border-subtle, #2a2a2a)',
  background: 'var(--surface-2)',
  color: 'var(--text-primary)',
};

/** Warning variant — the ref matches neither a connection nor the registry. */
const unknownChip: CSSProperties = {
  ...customChip,
  border: '1px solid var(--status-warning-bd, var(--status-warning, #a68a2d))',
  background: 'var(--status-warning-bg, var(--surface-2))',
  color: 'var(--status-warning, var(--text-primary))',
};

const removeBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  padding: 0,
  fontSize: 11,
  lineHeight: 1,
};

const pickerList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  maxHeight: 180,
  overflowY: 'auto',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm, 4px)',
  padding: 4,
};

const pickerRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  padding: '4px 6px',
  border: 'none',
  borderRadius: 'var(--radius-sm, 4px)',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
};

const pickerDescription: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  overflow: 'hidden',
};
